// Milestone times across time zones. Thread works in New Zealand time; an
// engagement with a headquarters elsewhere declares it in Finance Config
// (top level, like currency):
//   "timeZone": "America/Chicago", "timeZoneLabel": "Austin"
// A timed step stores its wall-clock time once, in the zone it was set in:
//   { date: "2026-11-02", time: "14:00", endTime: "15:00", tz: "America/Chicago" }
// and is shown in the client's zone and in New Zealand, each with its own
// weekday and date (Mon 2 Nov in Austin is Tue 3 Nov in Auckland).
// Steps with only a date are shown as a date, unconverted.
export const HOME_TIME_ZONE = "Pacific/Auckland";
export const HOME_TIME_ZONE_LABEL = "NZ";

export function isValidTimeZone(tz) {
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return true; } catch (e) { return false; }
}

// The zones an engagement is shown in: the client's first (it is their
// dashboard), then New Zealand. One zone when the client is in New Zealand.
export function zonesFor(cfg) {
  const tz = cfg && cfg.timeZone;
  if (!tz || tz === HOME_TIME_ZONE || !isValidTimeZone(tz)) return [{ tz: HOME_TIME_ZONE, label: HOME_TIME_ZONE_LABEL }];
  return [{ tz, label: cfg.timeZoneLabel || tz.split("/").pop().replace(/_/g, " ") }, { tz: HOME_TIME_ZONE, label: HOME_TIME_ZONE_LABEL }];
}

function parts(instant, tz) {
  const p = {};
  new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", weekday: "short" })
    .formatToParts(new Date(instant)).forEach(x => { p[x.type] = x.value; });
  return p;
}
function offsetMs(instant, tz) {
  const p = parts(instant, tz);
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return asUtc - Math.floor(instant / 1000) * 1000;
}

// Wall-clock date + time in a zone → epoch ms. Two passes settle the
// daylight-saving offset either side of a change.
export function zonedToInstant(date, time, tz) {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const wall = Date.UTC(y, m - 1, d, hh, mm);
  let t = wall - offsetMs(wall, tz);
  t = wall - offsetMs(t, tz);
  return t;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function clock(instant, tz) {
  const p = parts(instant, tz);
  const h = +p.hour % 24;
  return { h12: `${h % 12 || 12}:${p.minute}`, suffix: h < 12 ? "am" : "pm", day: `${p.weekday} ${+p.day} ${MONTHS[+p.month - 1]}` };
}

// "2:00–3:00pm", "11:30am–12:30pm", or "2:00pm" with no end.
export function formatRange(startMs, endMs, tz) {
  const a = clock(startMs, tz);
  if (endMs == null) return `${a.h12}${a.suffix}`;
  const b = clock(endMs, tz);
  return a.suffix === b.suffix ? `${a.h12}–${b.h12}${b.suffix}` : `${a.h12}${a.suffix}–${b.h12}${b.suffix}`;
}

// What a timed step shows: one line per zone. [] when the step has no time.
export function whenLines(step, zones) {
  if (!step || !step.date || !step.time) return [];
  const from = step.tz && isValidTimeZone(step.tz) ? step.tz : HOME_TIME_ZONE;
  const start = zonedToInstant(step.date, step.time, from);
  let end = null;
  if (step.endTime) {
    end = zonedToInstant(step.date, step.endTime, from);
    if (end <= start) end += 24 * 3600 * 1000; // runs past midnight
  }
  return zones.map(z => ({ label: zones.length > 1 ? z.label : "", day: clock(start, z.tz).day, time: formatRange(start, end, z.tz) }));
}

// Date-only steps keep their existing short form: "2 Nov".
export function formatStepDate(iso) {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}
